import { useCallback, useState } from "react";
import {
  BATCH_CHARACTERISTIC_DRAFT_VAZIO,
  type BatchCharacteristicDraft,
  type BatchCharacteristicResponse,
} from "@/types/batch-characteristics.types";

/** Spec 084 — o rascunho da característica e o que falta para ela poder ser salva. */
export function useCharacteristicDraft() {
  const [draft, setDraftState] = useState<BatchCharacteristicDraft>(
    BATCH_CHARACTERISTIC_DRAFT_VAZIO
  );

  const setDraft = useCallback((patch: Partial<BatchCharacteristicDraft>) => {
    setDraftState((d) => ({ ...d, ...patch }));
  }, []);

  const load = useCallback((item: BatchCharacteristicResponse) => {
    setDraftState({
      id: item.id,
      name: item.name,
      kind: item.kind,
      required: item.required,
      lineIds: item.lines.map((l) => l.id),
      values: item.values.map((v) => ({ id: v.id, label: v.label })),
    });
  }, []);

  const reset = useCallback(() => setDraftState(BATCH_CHARACTERISTIC_DRAFT_VAZIO), []);

  const labels = draft.values.map((v) => v.label.trim().toLowerCase()).filter(Boolean);
  const duplicated = labels.length !== new Set(labels).size;
  const problem =
    draft.name.trim() === ""
      ? "Dê um nome à característica."
      : draft.kind === "LIST" && labels.length === 0
        ? "Uma característica de lista precisa de ao menos um valor."
        : duplicated
          ? "Há valores repetidos na lista."
          : null;

  /** O que vai para a API: valores vazios caem, texto livre não leva lista. */
  function payload() {
    const values =
      draft.kind === "LIST"
        ? draft.values
            .filter((v) => v.label.trim() !== "")
            .map((v) => (v.id ? { id: v.id, label: v.label.trim() } : { label: v.label.trim() }))
        : [];
    const base = {
      name: draft.name.trim(),
      required: draft.required,
      lineIds: draft.lineIds,
      values,
    };
    return draft.id
      ? { id: draft.id, payload: base }
      : { id: null, payload: { ...base, kind: draft.kind } };
  }

  return { draft, setDraft, load, reset, problem, payload };
}
